import React from "react";
import { Icons } from "../common/DashboardComponents";
import StatusBadge from "../common/StatusBadge";
import { ContentCard, ActionLink } from "../common/DashboardComponents";

// Common icons
export const StatusIcons = {
  pendingIcon: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="12" y1="8" x2="12" y2="12"></line>
      <line x1="12" y1="16" x2="12.01" y2="16"></line>
    </svg>
  ),
  approvedIcon: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
      <polyline points="22 4 12 14.01 9 11.01"></polyline>
    </svg>
  ),
  rejectedIcon: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="15" y1="9" x2="9" y2="15"></line>
      <line x1="9" y1="9" x2="15" y2="15"></line>
    </svg>
  ),
  forwardedIcon: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12h13M12 5l7 7-7 7"></path>
    </svg>
  ),
};

// Common table columns - can be reused across different roles
export const TableColumns = {
  // Grievance columns
  grievanceIdColumn: {
    key: "id",
    label: "ID",
    render: (item) => `G${item.GRV_ID.toString().padStart(10, "0")}`,
  },
  grievanceCategoryColumn: {
    key: "category",
    label: "Category",
    render: (item) => item.GRV_CAT,
  },
  grievanceStatusColumn: {
    key: "status",
    label: "Status",
    render: (item) => <StatusBadge status={item.STATUS_NAME} />,
  },
  grievanceUserColumn: {
    key: "name",
    label: "Applicant",
    render: (item) => item.USER_NAME,
  },
  grievanceDetailsColumn: {
    key: "details",
    label: "Details",
    render: (item) => item.GRV_DETAILS || "N/A",
  },
  grievanceDateColumn: {
    key: "date",
    label: "Date",
    render: (item) => item.ENTRY_DATETIME || "N/A",
  },
  grievanceUnitColumn: {
    key: "unit",
    label: "Unit/Centre",
    render: (item) => item.UNIT_CENTRE_NAME || "N/A",
  },

  // Grant columns
  grantIdColumn: {
    key: "id",
    label: "ID",
    render: (item) => `C${item.APP_ID.toString().padStart(10, "0")}`,
  },
  grantRemarksColumn: {
    key: "remarks",
    label: "Remarks",
    render: (item) => item.REMARKS || "N/A",
  },
  grantSchemeColumn: {
    key: "scheme",
    label: "Scheme",
    render: (item) => item.GRANT_TYPE,
  },
  grantStatusColumn: {
    key: "status",
    label: "Status",
    render: (item) => <StatusBadge status={item.STATUS_NAME} isGrant={true} />,
  },
  grantUserColumn: {
    key: "name",
    label: "Applicant",
    render: (item) => item.USER_NAME,
  },
  grantDateColumn: {
    key: "date",
    label: "Date",
    render: (item) => item.ENTRY_DATETIME || "N/A",
  },
  grantAmountColumn: {
    key: "amount",
    label: "Amount",
    render: (item) => `₹${item.RELIEF_AMOUNT || "0"}`,
  },
  grantUnitColumn: {
    key: "unit",
    label: "Unit/Centre",
    render: (item) => item.UNIT_CENTRE_NAME || "N/A",
  },
  cardIDColumn: {
    key: "CARD_ID",
    label: "Card ID",
    render: (item) => item.CARD_ID || "N/A",
  },
  cardUserColumn: {
    key: "USER_NAME",
    label: "Applicant Name",
    render: (item) => item.USER_NAME || "N/A",
  },
  cardTypeColumn: {
    key: "IDCARD_TYPE_NAME",
    label: "Card Type",
    render: (item) => item.IDCARD_TYPE_NAME || "N/A",
  },
  cardCenterColumn: {
    key: "UNIT_CENTRE_NAME",
    label: "Center Name",
    render: (item) => item.UNIT_CENTRE_NAME || "N/A",
  },
  cardRemarkColumn: {
    key: "REMARKS",
    label: "Remarks ",
    render: (item) => item.REMARKS || "N/A",
  },
  cardDateColumn: {
    key: "ENTRY_DATETIME",
    label: "Date Applied",
    render: (item) => item.ENTRY_DATETIME || "N/A",
  },
  cardStatusColumn: {
    key: "STATUS_NAME",
    label: "Status",
    render: (item) => <StatusBadge status={item.STATUS_NAME} />,
  },
};

// Status definitions for filtering
export const STATUS = {
  GRIEVANCE: {
    RAISED: "1",
    ACCEPTED: "2",
    REJECTED: "3",
    IN_PROGRESS: "4",
    RESOLVED: ["Resolve", "Closed"],
  },
  GRANT: {
    APPLIED: "10",
    ACCEPTED: "11",
    REJECTED: "12",
    IN_PROGRESS: "13",
    FORWARDED: ["11", "13", "14", "15", "16", "17"],
    PENDING: ["11", "13", "14", "15"],
    APPROVED: "16",
    REJECTED_FINAL: "17",
  },
};

// Helper functions for data filtering
export const filterHelpers = {
  isGrievanceForwarded: (grievance) =>
    grievance.ISFORWARDED === "1" || grievance.ISFORWARDED === 1,

  isGrantForwarded: (grant) => STATUS.GRANT.FORWARDED.includes(grant.STATUS_ID),

  isPendingGrievance: (grievance) =>
    grievance.STATUS_NAME?.toLowerCase() === "pending" ||
    grievance.STATUS_ID === STATUS.GRIEVANCE.IN_PROGRESS,
    
  isCompletedGrievance: (grievance) =>
    grievance.STATUS_NAME?.toLowerCase() === "Completed" ||
    grievance.STATUS_ID === 7 || grievance.STATUS_ID === "7",

  isPendingGrant: (grant) => STATUS.GRANT.PENDING.includes(grant.STATUS_ID),

  isResolvedGrievance: (grievance) =>
    STATUS.GRIEVANCE.RESOLVED.includes(grievance.STATUS_NAME),

  isApprovedGrant: (grant) => grant.STATUS_ID === STATUS.GRANT.APPROVED,

  isRejectedGrant: (grant) => grant.STATUS_ID === STATUS.GRANT.REJECTED_FINAL,
};

// SM code for getRoleConfig
export const getRoleConfig = (
  userRole,
  grievances,
  grants,
  centerId,
  idCard,
  navigate
) => {
  // Filter data based on role
  let filteredGrievances = grievances;
  let filteredGrants = grants;

  // Stats calculation for all roles
  const totalGrievances = grievances.length;
  const totalGrants = grants.length;
  const totalIdCard = idCard.length;

  // ARESA Center (Role 2)
  if (userRole === 2) {
    return {
      title: "ARESA Center Dashboard",
      statCards: [
        {
          title: "Total Grievances",
          value: totalGrievances,
          icon: Icons.Grievances,
          color: "#3949ab",
          link: "/GrievanceList",
        },
        {
          title: "Total Grants",
          value: totalGrants,
          icon: Icons.Grants,
          color: "#9C27B0",
          link: "/GrantList",
        },
      ],
      tables: [
        {
          title: "Grievances",
          icon: Icons.Grievances,
          linkTo: "/GrievanceList",
          data: grievances,
          columns: [
            TableColumns.grievanceIdColumn,
            TableColumns.grievanceUserColumn,
            TableColumns.grievanceCategoryColumn,
            TableColumns.grievanceStatusColumn,
          ],
        },
        {
          title: "Grants",
          icon: Icons.Grants,
          linkTo: "/GrantList",
          data: grants,
          columns: [
            TableColumns.grantIdColumn,
            TableColumns.grantUserColumn,
            TableColumns.grantSchemeColumn,
            TableColumns.grantStatusColumn,
          ],
        },
      ],
    };
  }

  // Branch (Role 3)
  else if (userRole === 3) {
    // Filter data for Branch
    filteredGrievances = grievances.filter(filterHelpers.isGrievanceForwarded);
    filteredGrants = grants.filter(filterHelpers.isGrantForwarded);

    // Calculate stats
    const forwardedGrievances = grievances?.length;
    const pendingGrievances = filteredGrievances.filter(
      filterHelpers.isPendingGrievance
    ).length;
    const resolvedGrievances = filteredGrievances.filter(
      filterHelpers.isResolvedGrievance
    ).length;
    const forwardedGrants = grants?.length;
    const pendingGrants = filteredGrants.filter(
      filterHelpers.isPendingGrant
    ).length;
    const approvedGrants = filteredGrants.filter(
      filterHelpers.isApprovedGrant
    ).length;
    const rejectedGrants = filteredGrants.filter(
      filterHelpers.isRejectedGrant
    ).length;

    return {
      title: "Branch Dashboard",
      statCards: [
        {
          title: "Total Grievances",
          value: forwardedGrievances,
          icon: Icons.Grievances,
          color: "#3949ab",
          link: "/GrievanceList",
          additionalInfo: `Pending: ${pendingGrievances} | Resolved: ${resolvedGrievances}`,
        },
        ...(centerId === "1"
          ? [
              {
                title: "Pending Grievances",
                value: pendingGrievances,
                icon: StatusIcons.pendingIcon,
                color: "#FF9800",
                additionalInfo: `Pending: ${pendingGrievances} | Resolved: ${resolvedGrievances}`,
              },
              {
                title: "Total Grants",
                value: forwardedGrants,
                icon: Icons.Grants,
                color: "#9C27B0",
                link: "/GrantList",
                additionalInfo: `Approved: ${approvedGrants} | Rejected: ${rejectedGrants}`,
              },
              {
                title: "Pending Grants",
                value: pendingGrants,
                icon: StatusIcons.pendingIcon,
                color: "#FF9800",
                additionalInfo: `Approved: ${approvedGrants} | Rejected: ${rejectedGrants}`,
              },
            ]
          : []),
      ],
      tables: [
        {
          title: "Total Grievances",
          icon: Icons.Grievances,
          linkTo: "/GrievanceList",
          data: grievances,
          columns: [
            TableColumns.grievanceIdColumn,
            TableColumns.grievanceUserColumn,
            TableColumns.grievanceCategoryColumn,
            TableColumns.grievanceDateColumn,
            TableColumns.grievanceStatusColumn,
          ],
        },

        ...(centerId === "1"
          ? [
              {
                title: "Grant Applications",
                icon: Icons.Grants,
                linkTo: "/GrantList",
                data: grants,
                columns: [
                  TableColumns.grantIdColumn,
                  TableColumns.grantUserColumn,
                  TableColumns.grantSchemeColumn,
                  TableColumns.grantDateColumn,
                  TableColumns.grantStatusColumn,
                ],
              },
            ]
          : []),
      ],
    };
  }

  // Section User 1 (Role 9)
  else if (userRole === 9) {
    const pendingGrievances = grievances.filter(
      filterHelpers.isPendingGrievance
    ).length;

    return {
      title: "Section User Dashboard",
      statCards: [
        {
          title: "Total Grievances",
          value: totalGrievances,
          icon: Icons.Grievances,
          color: "#3949ab",
          link: "/GrievanceList",
        },
      ],
      tables: [
        {
          title: "Grievances",
          icon: Icons.Grievances,
          linkTo: "/GrievanceList",
          data: grievances,
          columns: [
            TableColumns.grievanceIdColumn,
            TableColumns.grievanceUserColumn,
            TableColumns.grievanceCategoryColumn,
            TableColumns.grievanceStatusColumn,
          ],
        },
      ],
    };
  }
  //  Section User 2 (Role 10)
  else if (userRole === 10) {
    const approvedGrants = grants.filter(filterHelpers.isApprovedGrant).length;

    return {
      title: "Section User Dashboard",
      statCards: [
        {
          title: "Total Grants",
          value: totalGrants,
          icon: Icons.Grants,
          color: "#9C27B0",
          link: "/GrantList",
        },
      ],
      tables: [
        {
          title: "Grants",
          icon: Icons.Grants,
          linkTo: "/GrantList",
          data: grants,
          columns: [
            TableColumns.grantIdColumn,
            TableColumns.grantUserColumn,
            TableColumns.grantSchemeColumn,
            TableColumns.grantStatusColumn,
          ],
        },
      ],
    };
  } else if (userRole === 11) {
    return {
      title: "Id Card Deshboard",
      statCards: [
        {
          title: "Total Card",
          value: totalIdCard,
          icon: Icons.Grants,
          color: "#9C27B0",
          link: "/IdCardList",
        },
      ],
      tables: [
        {
          title: "ID Card",
          icon: Icons.Grants,
          linkTo: "/IdCardList",
          data: idCard,
          columns: [
            TableColumns.cardIDColumn,
            TableColumns.cardUserColumn,
            TableColumns.cardTypeColumn,
            TableColumns.cardCenterColumn,
            TableColumns.cardRemarkColumn,
            TableColumns.cardDateColumn,
            TableColumns.cardStatusColumn,
          ],
        },
      ],
    };
  }

  // DGAR (Role 4), COLA (Role 5), BRIG PERS (Role 6), ADG (Role 7), DGAR (Role 8)
  else {
    // Calculate stats
    console.log(grievances,"grievances123");

    const pendingGrievances = grievances.filter(
      filterHelpers.isCompletedGrievance
    ).length;
    const approvedGrants = grants.filter(filterHelpers.isApprovedGrant).length;

    // Set title based on role
    let dashboardTitle = "Dashboard";
    switch (userRole) {
      case 4:
        dashboardTitle = "DGAR Dashboard";
        break;
      case 5:
        dashboardTitle = "COLA Dashboard";
        break;
      case 6:
        dashboardTitle = "BRIG PERS Dashboard";
        break;
      case 7:
        dashboardTitle = "ADG Dashboard";
        break;
      case 8:
        dashboardTitle = "DGAR Dashboard";
        break;
      default:
        dashboardTitle = "Dashboard";
    }

    return {
      title: dashboardTitle,
      statCards: [
        {
          title: "Total Grievances",
          value: totalGrievances,
          icon: Icons.Grievances,
          color: "#3949ab",
          link: "/GrievanceList",
        },
        {
          title: "Completed Grievances",
          value: pendingGrievances,
          icon: StatusIcons.approvedIcon,
          color: "#4CAF50", 
          link: "/GrievanceList",
          onClick: () => {
            navigate("/GrievanceList?isCompleted=true");
          }
        },
        // {
        //   title: "Pending Grievances",
        //   value: pendingGrievances,
        //   icon: StatusIcons.pendingIcon,
        //   color: "#FF9800",
        // },
        {
          title: "Total Grants",
          value: totalGrants,
          icon: Icons.Grants,
          color: "#9C27B0",
          link: "/GrantList",
        },
        {
          title: "Approved Grants",
          value: approvedGrants,
          icon: StatusIcons.approvedIcon,
          color: "#4CAF50",
        },
      ],
      tables: [
        {
          title: "All Grievances",
          icon: Icons.Grievances,
          linkTo: "/GrievanceList",
          data: grievances,
          columns: [
            TableColumns.grievanceIdColumn,
            TableColumns.grievanceUserColumn,
            TableColumns.grievanceUnitColumn,
            TableColumns.grievanceCategoryColumn,
            TableColumns.grievanceDateColumn,
            TableColumns.grievanceStatusColumn,
          ],
        },
        {
          title: "All Grant Applications",
          icon: Icons.Grants,
          linkTo: "/GrantList",
          data: grants,
          columns: [
            TableColumns.grantIdColumn,
            TableColumns.grantUserColumn,
            TableColumns.grantUnitColumn,
            TableColumns.grantSchemeColumn,
            TableColumns.grantAmountColumn,
            TableColumns.grantStatusColumn,
          ],
        },
      ],
    };
  }
};

// Render a single table based on the provided configuration
export const renderTable = (tableConfig) => {
  if (!tableConfig || !tableConfig.data) {
    return null;
  }
  console.log(tableConfig, "tableConfig");
  return (
    <ContentCard
      key={tableConfig.title}
      title={tableConfig.title}
      icon={tableConfig.icon}
      action={<ActionLink to={tableConfig.linkTo} text="View All" />}
    >
      <div className="table-responsive">
        <table className="table border-top border-bottom">
          <thead className="bg-light">
            <tr>
              {tableConfig.columns.map((col) => (
                <th className="py-3" key={col.key}>
                  <div className="d-flex align-items-center">
                    <span>{col.label}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableConfig.data.length === 0 ? (
              <tr>
                <td
                  colSpan={tableConfig.columns.length}
                  className="text-center py-4"
                >
                  No {tableConfig.title.toLowerCase()} found
                </td>
              </tr>
            ) : (
              tableConfig.data.slice(0, 5).map((item, index) => (
                <tr
                  key={`${index}_${item.GRV_ID || item.APP_ID}`}
                  className="border-bottom"
                >
                  {tableConfig.columns.map((col) => (
                    <td className="py-3" key={`${index}_${col.key}`}>
                      {col.render(item)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </ContentCard>
  );
};
